import React from 'react';
import { DeviceThermostatOutlined } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

// Какие датчики что измеряют. У Antminer, ElphaPex и WhatsMiner M5x T0–T2 — чипы хеш-плат 1–3, T3–T5 — сами платы.
// WhatsMiner M6x отдаёт только температуру плат, и приходит она в T0–T2 (T3–T5 пусты); в справочнике такие
// машины могут значиться просто как «WhatsMiner», поэтому смотрим и на модель, и на наличие T3–T5.
export const temperatureLayout = (model, hasUpperSensors) => {
  const name = String(model || '');
  if (/whatsminer\s*m6\d/i.test(name) || (/whatsminer/i.test(name) && !hasUpperSensors)) {
    return { chips: [], boards: [0, 1, 2] };
  }
  return { chips: [0, 1, 2], boards: [3, 4, 5] };
};

// Запись под курсором графика берём, только если она близка по времени: иначе показали бы чужой момент
const HOVER_MAX_GAP_MS = 10 * 60 * 1000;

const Temperature = ({ temperatureData, metricsData, hoveredTimestamp, isDeviceOnline, alertRules, model, staleNote }) => {
  const theme = useTheme();
  const { t } = useTranslation('dashboard');

  const findMetricsByTimestamp = (timestamp) => {
    if (!Array.isArray(metricsData) || metricsData.length === 0) return null;
    const target = new Date(timestamp).getTime();
    let closest = null;
    let minDiff = Infinity;
    for (const metric of metricsData) {
      const diff = Math.abs(new Date(metric.created_at).getTime() - target);
      if (diff < minDiff) { minDiff = diff; closest = metric; }
    }
    return minDiff <= HOVER_MAX_GAP_MS ? closest : null;
  };

  const hovered = hoveredTimestamp ? findMetricsByTimestamp(hoveredTimestamp) : null;
  const source = hovered || temperatureData;

  const read = (i) => {
    const v = source?.[`temperature${i}`];
    return v === null || v === undefined || isNaN(Number(v)) ? null : Math.round(Number(v));
  };
  const limit = (i) => {
    const v = alertRules?.[`temperature${i}_max`];
    return v === null || v === undefined ? null : Number(v);
  };

  const present = (data, i) => data?.[`temperature${i}`] !== null && data?.[`temperature${i}`] !== undefined;
  const hasUpperSensors = [3, 4, 5].some((i) => present(temperatureData, i) || present(source, i));
  const layout = temperatureLayout(model, hasUpperSensors);
  const columns = [
    { key: 'chips', title: t('tempChips'), sensors: layout.chips },
    { key: 'boards', title: t('tempBoards'), sensors: layout.boards },
  ].filter((c) => c.sensors.some((i) => read(i) !== null));
  const rows = Math.max(0, ...columns.map((c) => c.sensors.length));

  const titleColor = !isDeviceOnline ? theme.palette.text.disabled : theme.palette.text.primary;
  const muted = theme.palette.text.secondary;
  const trackBg = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)';
  const amber = theme.palette.mode === 'dark' ? '#f5b041' : '#c77700';

  // Цвет — по близости к лимиту из правил алертов: до 85% — норма, до лимита — внимание, выше — превышение
  const colorFor = (value, max) => {
    if (!isDeviceOnline) return theme.palette.text.disabled;
    const m = max ?? 90;
    if (value > m) return theme.palette.contrast.red;
    if (value >= m * 0.85) return amber;
    return theme.palette.contrast.green;
  };

  const limitLabel = (sensors) => {
    const limits = [...new Set(sensors.map(limit).filter((v) => v !== null))];
    if (limits.length === 0) return t('tempNoLimit');
    if (limits.length === 1) return t('tempLimit', { value: limits[0] });
    return t('tempLimitRange', { min: Math.min(...limits), max: Math.max(...limits) });
  };

  const grid = { display: 'grid', gridTemplateColumns: `64px repeat(${columns.length}, minmax(0, 1fr))`, columnGap: '16px', alignItems: 'center' };

  return (
    <div id="temperature-container" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', fontSize: '18px', fontWeight: 600, color: titleColor, marginBottom: '10px' }}>
        <DeviceThermostatOutlined style={{ marginRight: '8px', fontSize: '20px', color: titleColor }} />
        <span style={{ whiteSpace: 'nowrap' }}>{t('tempTitle')}</span>
        {staleNote && !hovered && (
          <span style={{ marginLeft: '10px', fontSize: '12px', fontWeight: 400, color: theme.palette.text.secondary, whiteSpace: 'nowrap' }}>{staleNote}</span>
        )}
      </div>
      <div style={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: '10px',
        padding: '14px 16px',
        boxShadow: theme.shadows[1],
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: '12px',
        fontVariantNumeric: 'tabular-nums',
      }}>
        {columns.length > 0 ? (
          <>
            <div style={grid}>
              <div />
              {columns.map((c) => (
                <div key={c.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }}>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: titleColor }}>{c.title}</span>
                  <span style={{ fontSize: '12px', color: muted, whiteSpace: 'nowrap' }}>{limitLabel(c.sensors)}</span>
                </div>
              ))}
            </div>
            {Array.from({ length: rows }, (_, row) => (
              <div key={row} style={grid}>
                <div style={{ fontSize: '13px', color: muted, whiteSpace: 'nowrap' }}>{t('tempHashboard', { n: row + 1 })}</div>
                {columns.map((c) => {
                  const index = c.sensors[row];
                  const value = index === undefined ? null : read(index);
                  if (value === null) {
                    return <div key={c.key} style={{ fontSize: '14px', color: theme.palette.text.disabled, textAlign: 'right' }}>—</div>;
                  }
                  const max = limit(index);
                  const color = colorFor(value, max);
                  const width = Math.min(100, Math.max(4, (value / (max ?? 90)) * 100));
                  return (
                    <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: '10px' }} title={max !== null ? t('tempLimit', { value: max }) : undefined}>
                      <div style={{ flex: 1, height: '10px', borderRadius: '5px', background: trackBg, overflow: 'hidden' }}>
                        <div style={{ width: `${width}%`, height: '100%', background: color, borderRadius: '5px', transition: 'width .3s ease' }} />
                      </div>
                      <div style={{ width: '40px', flexShrink: 0, textAlign: 'right', fontSize: '15px', fontWeight: 700, color }}>{value}°</div>
                    </div>
                  );
                })}
              </div>
            ))}
            {hoveredTimestamp && !hovered && (
              <div style={{ fontSize: '12px', color: muted }}>{t('tempNoHoverData')}</div>
            )}
          </>
        ) : (
          <div style={{ textAlign: 'center', color: !isDeviceOnline ? theme.palette.text.disabled : muted, fontSize: '14px' }}>
            {t('tempNoData')}
          </div>
        )}
      </div>
    </div>
  );
};

export default Temperature;
